import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAnalytics, getStats } from '../../services/apiService';

const AdminPanel = ({ user }) => {
  const [analytics, setAnalytics] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('7days'); // 7days, 30days, 90days
  const navigate = useNavigate();

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('adminToken');
      console.log('🔐 Token check in AdminPanel:', { 
        hasToken: !!token,
        tokenLength: token ? token.length : 0 
      });
      
      if (!token) {
        throw new Error('Authentication token missing. Please login again.');
      }
      
      // Fetch both analytics and stats in parallel
      const [analyticsData, statsData] = await Promise.all([
        getAnalytics(timeRange),
        getStats(timeRange)
      ]);
      
      setAnalytics(analyticsData);
      setStats(statsData);
    } catch (err) {
      console.error('Analytics fetch error:', err);
      if (err.message.includes('Authentication') || err.message.includes('401') || err.message.includes('token')) {
        console.log('🔐 Authentication failed, redirecting to login');
        handleLogout();
      } else {
        setError('Failed to fetch analytics data. Please try again.');
        // Set default analytics structure if fetch fails
        setAnalytics({
          overview: {
            totalLeads: 0,
            newLeads: 0,
            contactedLeads: 0,
            quotedLeads: 0,
          },
          charts: {
            statusDistribution: {},
            serviceDistribution: {},
          },
          recentActivity: [],
          topServices: {},
          topPerformers: [],
          leadSources: {},
          conversionFunnel: {},
          revenueMetrics: {}
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/login');
  };

  // Calculate percentages for charts
  const calculatePercentage = (count, total) => {
    if (total === 0) return 0;
    return Math.round((count / total) * 100);
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-green-500',
      contacted: 'bg-blue-500',
      quoted: 'bg-purple-500',
      won: 'bg-emerald-500',
      lost: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-red-500',
      medium: 'bg-yellow-500',
      low: 'bg-green-500'
    };
    return colors[priority] || 'bg-gray-500';
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Get time range label
  const getTimeRangeLabel = () => {
    const labels = {
      '7days': 'Last 7 Days',
      '30days': 'Last 30 Days',
      '90days': 'Last 90 Days'
    };
    return labels[timeRange] || 'Last 7 Days';
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400">Loading analytics...</p>
      </div>
    </div>
  );

  const displayStats = stats || {
    totalLeads: analytics?.overview?.totalLeads || 0,
    leadsToday: 0,
    conversionRate: 0,
    averageQuote: 0,
    highPriorityLeads: 0,
    totalRevenue: 0,
    wonDeals: 0,
    responseRate: 0
  };

  const totalLeads = analytics?.overview?.totalLeads || 0;
  const topPerformers = analytics?.topPerformers || [];
  const leadSources = analytics?.leadSources || {};
  const conversionFunnel = analytics?.conversionFunnel || {};
  const revenueMetrics = analytics?.revenueMetrics || {};

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 text-white rounded-2xl p-8 mb-8 shadow-xl">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">Analytics Dashboard</h1>
              <p className="text-white/90">Welcome back, {user.username} • {getTimeRangeLabel()}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <select 
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="bg-white/20 backdrop-blur-sm border border-white/30 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-white/50"
              >
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
                <option value="90days">Last 90 Days</option>
              </select>
              <button 
                onClick={() => navigate('/dashboard')}
                className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105 flex items-center gap-2 backdrop-blur-sm"
              >
                📊 Manage Leads
              </button>
              <button 
                onClick={() => navigate('/form')}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105 flex items-center gap-2"
              >
                ➕ Create Lead
              </button>
              <button 
                onClick={fetchAnalyticsData}
                className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105 flex items-center gap-2 backdrop-blur-sm"
              >
                🔄 Refresh
              </button>
            </div>
          </div>
        </header>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-xl shadow-lg transition-transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-3xl font-bold mb-1">{displayStats.totalLeads}</h3>
                <p className="text-blue-100 font-medium">Total Leads</p>
              </div>
              <div className="text-3xl">📈</div>
            </div>
            <div className="mt-2 text-blue-100 text-sm">
              {displayStats.leadsToday} new today
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-xl shadow-lg transition-transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-3xl font-bold mb-1">{displayStats.conversionRate}%</h3>
                <p className="text-green-100 font-medium">Conversion Rate</p>
              </div>
              <div className="text-3xl">🎯</div>
            </div>
            <div className="mt-2 text-green-100 text-sm">
              {displayStats.wonDeals || 0} deals won
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-xl shadow-lg transition-transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-3xl font-bold mb-1">{formatCurrency(displayStats.averageQuote)}</h3>
                <p className="text-purple-100 font-medium">Avg Quote</p>
              </div>
              <div className="text-3xl">💰</div>
            </div>
            <div className="mt-2 text-purple-100 text-sm">
              {formatCurrency(displayStats.totalRevenue || 0)} total
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-6 rounded-xl shadow-lg transition-transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-3xl font-bold mb-1">{displayStats.highPriorityLeads}</h3>
                <p className="text-orange-100 font-medium">High Priority</p>
              </div>
              <div className="text-3xl">⚠️</div>
            </div>
            <div className="mt-2 text-orange-100 text-sm">
              {displayStats.responseRate || 0}% response rate
            </div>
          </div>
        </div>

        {/* Main Analytics Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Status Distribution */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm lg:col-span-2">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Lead Status Distribution</h3>
            <div className="space-y-4">
              {Object.entries(analytics?.charts?.statusDistribution || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(status)}`}></div>
                    <span className="text-gray-600 dark:text-gray-400 capitalize font-medium">{status}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                      <div 
                        className={`h-3 rounded-full transition-all duration-500 ${getStatusColor(status)}`}
                        style={{ 
                          width: `${calculatePercentage(count, totalLeads)}%` 
                        }}
                      ></div>
                    </div>
                    <span className="font-semibold text-gray-900 dark:text-white w-12 text-right">
                      {count} ({calculatePercentage(count, totalLeads)}%)
                    </span>
                  </div>
                </div>
              ))}
              {(!analytics?.charts?.statusDistribution || Object.keys(analytics.charts.statusDistribution).length === 0) && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <div className="text-4xl mb-2">📊</div>
                  <p>No status data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Top Performers */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">🏆 Top Performers</h3>
            <div className="space-y-4">
              {topPerformers.map((performer, index) => (
                <div key={performer.name} className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                      index === 0 ? 'bg-yellow-500' : 
                      index === 1 ? 'bg-gray-400' : 
                      index === 2 ? 'bg-orange-500' : 'bg-blue-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{performer.name}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {performer.leads} leads • {performer.conversionRate}% conversion
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-green-600 dark:text-green-400">
                      {formatCurrency(performer.revenue)}
                    </div>
                  </div>
                </div>
              ))}
              {topPerformers.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <div className="text-4xl mb-2">👥</div>
                  <p>No performance data</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Second Row Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Lead Sources */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">🌐 Lead Sources</h3>
            <div className="space-y-4">
              {Object.entries(leadSources).map(([source, data]) => (
                <div key={source} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                      <span className="text-blue-600 dark:text-blue-400 text-sm">🔗</span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white capitalize">
                        {source.replace(/-/g, ' ')}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {data.conversionRate}% conversion
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {data.count}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {calculatePercentage(data.count, totalLeads)}%
                    </div>
                  </div>
                </div>
              ))}
              {Object.keys(leadSources).length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <div className="text-4xl mb-2">🌐</div>
                  <p>No lead source data</p>
                </div>
              )}
            </div>
          </div>

          {/* Conversion Funnel */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">🔄 Conversion Funnel</h3>
            <div className="space-y-4">
              {Object.entries(conversionFunnel).map(([stage, count]) => (
                <div key={stage} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${
                      stage === 'new' ? 'bg-green-500' :
                      stage === 'contacted' ? 'bg-blue-500' :
                      stage === 'quoted' ? 'bg-purple-500' :
                      stage === 'won' ? 'bg-emerald-500' : 'bg-gray-500'
                    }`}></div>
                    <span className="text-gray-600 dark:text-gray-400 capitalize font-medium">{stage}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${calculatePercentage(count, totalLeads)}%` 
                        }}
                      ></div>
                    </div>
                    <span className="font-semibold text-gray-900 dark:text-white w-12 text-right">
                      {count}
                    </span>
                  </div>
                </div>
              ))}
              {Object.keys(conversionFunnel).length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <div className="text-4xl mb-2">🔄</div>
                  <p>No funnel data</p>
                </div>
              )}
            </div>
          </div>

          {/* Revenue Metrics */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">💸 Revenue Metrics</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg">
                <div className="text-green-600 dark:text-green-400 font-semibold">Total Revenue</div>
                <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                  {formatCurrency(revenueMetrics.total || 0)}
                </div>
              </div>
              <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg">
                <div className="text-blue-600 dark:text-blue-400 font-semibold">Avg Deal Size</div>
                <div className="text-xl font-bold text-blue-700 dark:text-blue-300">
                  {formatCurrency(revenueMetrics.averageDealSize || 0)}
                </div>
              </div>
              <div className="flex justify-between items-center p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg">
                <div className="text-purple-600 dark:text-purple-400 font-semibold">Won Deals</div>
                <div className="text-xl font-bold text-purple-700 dark:text-purple-300">
                  {revenueMetrics.wonDeals || 0}
                </div>
              </div>
              <div className="flex justify-between items-center p-3 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-lg">
                <div className="text-orange-600 dark:text-orange-400 font-semibold">Pipeline Value</div>
                <div className="text-xl font-bold text-orange-700 dark:text-orange-300">
                  {formatCurrency(revenueMetrics.pipelineValue || 0)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Third Row Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Top Services */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">🔧 Top Services</h3>
            <div className="space-y-4">
              {Object.entries(analytics?.topServices || {}).map(([service, count]) => (
                <div key={service} className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                      <span className="text-purple-600 dark:text-purple-400 text-lg">🛠️</span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white capitalize">
                        {service.replace(/([A-Z])/g, ' $1').trim()}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {calculatePercentage(count, totalLeads)}% of total
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900 dark:text-white text-lg">
                      {count}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      leads
                    </div>
                  </div>
                </div>
              ))}
              {(!analytics?.topServices || Object.keys(analytics.topServices).length === 0) && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <div className="text-4xl mb-2">🔧</div>
                  <p>No service data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">📈 Recent Activity</h3>
            <div className="space-y-3">
              {analytics?.recentActivity && analytics.recentActivity.length > 0 ? (
                analytics.recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${getPriorityColor(activity.priority)}`}></div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{activity.customerName}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                          {activity.service} • {activity.leadOrigin}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        activity.status === 'new' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                        activity.status === 'contacted' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' :
                        activity.status === 'quoted' ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' :
                        activity.status === 'won' ? 'bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200' :
                        'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                      }`}>
                        {activity.status}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {new Date(activity.date).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <div className="text-4xl mb-2">📭</div>
                  <p>No recent activity</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-500 text-white p-4 rounded-lg mb-6">
            <div className="flex justify-between items-center">
              <span>{error}</span>
              <button onClick={() => setError('')} className="text-white hover:text-red-100">
                ×
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;